import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TopUpDialog } from '../dialogs/TopUpDialog'
import { PaymentMethodsSection } from './PaymentMethodsSection'
import { BillingInvoiceList } from './billing/BillingInvoiceList'
import { BillingTransactionList } from './billing/BillingTransactionList'
import { RecurringTopUpSelector } from './billing/RecurringTopUpSelector'
import { useWalletBalance } from '@/hooks/useWalletBalance'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { formatUSD } from '@/lib/format'

export function BillingsSection() {
  const { balance, isLoading, error, isLowBalance, refetch } = useWalletBalance()
  const topUpOpen = useSettingsStore((state) => state.topUpOpen)
  const setTopUpOpen = useSettingsStore((state) => state.setTopUpOpen)
  const amount = balance === null ? NaN : Number(balance)
  const available = !error && Number.isFinite(amount)

  return (
    <div className="flex flex-col gap-6 p-4 sm:p-6">
      <div>
        <h3 className="text-base font-semibold">Billing</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage credits, monthly top-ups, and payment methods.
        </p>
      </div>
      <div className="bg-muted/30 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border p-5">
        <div aria-live="polite">
          <p className="text-sm text-muted-foreground">Available credits</p>
          <p className="mt-1 text-3xl font-semibold tabular-nums">
            {available ? formatUSD(amount) : '—'}
          </p>
          {error ? (
            <div className="mt-2 flex items-center gap-2" role="alert">
              <span className="text-sm text-destructive">Balance unavailable</span>
              <Button variant="ghost" size="sm" onClick={refetch}>
                Retry balance
              </Button>
            </div>
          ) : isLoading && !available ? (
            <p className="mt-2 text-sm text-muted-foreground">Loading balance…</p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">
              {available && amount <= 0
                ? 'Add credits to run AI generations.'
                : isLowBalance
                  ? 'Your balance is running low.'
                  : 'Pay as you go. Credits are charged per generation in USD.'}
            </p>
          )}
        </div>
        <Button onClick={() => setTopUpOpen(true)}>
          <Icon name="plus" className="mr-2 h-4 w-4" />
          Add credits
        </Button>
      </div>
      <RecurringTopUpSelector />
      <Tabs defaultValue="payment-methods" className="min-w-0">
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="payment-methods">Payment methods</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="invoices">Invoices</TabsTrigger>
        </TabsList>
        <TabsContent value="payment-methods">
          <PaymentMethodsSection />
        </TabsContent>
        <TabsContent value="transactions">
          <BillingTransactionList />
        </TabsContent>
        <TabsContent value="invoices">
          <BillingInvoiceList />
        </TabsContent>
      </Tabs>
      {topUpOpen && (
        <TopUpDialog
          open={topUpOpen}
          onOpenChange={setTopUpOpen}
          currentBalance={available ? amount : null}
        />
      )}
    </div>
  )
}
